import { useCallback } from 'react';

import { ENDPOINTS, type ListPipelineRunsResponse } from '@clm/shared-types';

import { listPipelineRuns } from '../../api/pipeline-runs';
import { mayAttempt } from '../../auth/permissions';
import { useIdentity } from '../../auth/useIdentity';
import { PIPELINE_FORBIDDEN_COPY } from '../pipeline-status-chips';
import { PanelProblem } from '../quality/panel';
import { usePanel } from '../quality/panel-state';
import { ActiveRunCard } from './ActiveRunCard';
import { PendingRuns } from './PendingRuns';
import { RequestRunForm } from './RequestRunForm';
import { RescanCloudeqEntityCard } from './RescanCloudeqEntityCard';
import { RunHistory } from './RunHistory';
import { ScheduleCard } from './ScheduleCard';
import { APPROVE_ROLES } from './shared';

/**
 * The **Runs** section at the top of the Pipeline page — CLM-83 (R-83-1).
 *
 * One read, `GET /v1/pipeline/runs`, on `contract:read` (everyone, D-83-15);
 * the five components below act through the API and call `reload()` — no row
 * is patched in place. When the active run turns terminal the parent's
 * `onRunFinished` (the status section's `reload`) fires so the stage tiles
 * refresh with what the run wrote.
 *
 * The Schedule card is hidden for an identity that is known not to be an admin
 * (display hint; the API's `pipeline:approve` gate decides, NFR-06).
 */

const PANEL = 'pipeline runs';
const ENDPOINT_PATH = `${ENDPOINTS['list-pipeline-runs'].method} ${ENDPOINTS['list-pipeline-runs'].path}`;

export function PipelineRunsSection({ onRunFinished }: { readonly onRunFinished: () => void }) {
  const { identity } = useIdentity();
  const mayManageSchedule = mayAttempt(identity, APPROVE_ROLES);
  const { state, reload } = usePanel<ListPipelineRunsResponse>((abort) => listPipelineRuns(abort), []);

  const finished = useCallback(() => {
    reload();
    onRunFinished();
  }, [reload, onRunFinished]);

  return (
    <section className="card" aria-labelledby="pipeline-runs-heading">
      <h3 id="pipeline-runs-heading">Runs</h3>
      <p className="mut">
        Routine extraction runs are requested here, approved by an admin, and run by the API&rsquo;s own worker under its
        identity — one at a time, each with a recorded history. The <code>run-pipeline</code> workflow remains as break-glass
        only.
      </p>

      <RequestRunForm onDone={reload} />

      {state.kind === 'loading' ? (
        <p role="status" aria-busy="true">
          Reading {PANEL}…
        </p>
      ) : null}
      {state.kind === 'problem' ? (
        <PanelProblem panel={PANEL} state={state} endpointPath={ENDPOINT_PATH} onRetry={reload} forbiddenCopy={PIPELINE_FORBIDDEN_COPY} />
      ) : null}
      {state.kind === 'ready' ? (
        <>
          {state.response.active === null ? null : (
            <ActiveRunCard key={state.response.active.pipeline_run_id} run={state.response.active} onFinished={finished} />
          )}
          <h4>Pending</h4>
          <PendingRuns runs={state.response.pending} onDone={reload} />
          <h4>History</h4>
          <RunHistory runs={state.response.history} />
        </>
      ) : null}

      <div hidden={!mayManageSchedule}>{mayManageSchedule ? <ScheduleCard /> : null}</div>
      {/* CLM-91 (D-91-7) — the admin Rescan over the converted corpus; the same admin gate as the schedule. */}
      <div hidden={!mayManageSchedule}>{mayManageSchedule ? <RescanCloudeqEntityCard /> : null}</div>
    </section>
  );
}
